import { useNavigate } from "react-router-dom";
import { Video, Phone, Languages, CalendarClock, UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { VerifiedBadge, SampleBadge } from "@/components/common";
import { PriceTag } from "@/components/PriceTag";
import { RatingBadge } from "@/components/DoctorReviews";
import { fileUrl, fmtDT } from "@/lib/api";

export const DoctorAvatar = ({ d, size = "h-14 w-14" }) =>
  d.photo_url ? <img src={fileUrl(d.photo_url)} alt={d.full_name} className={`${size} rounded-2xl object-cover`} />
    : <div className={`${size} rounded-2xl bg-[#086477]/10 flex items-center justify-center`}><UserRound className="h-6 w-6 text-[#086477]" /></div>;

export default function DoctorCard({ d, bookPath }) {
  const nav = useNavigate();
  return (
    <div className="surface p-5 flex flex-col gap-4 hover:border-[#086B2D]/40 hover:-translate-y-0.5 transition-[transform,border-color] duration-200" data-testid={`doctor-card-${d.id}`}>
      <div className="flex gap-4">
        <DoctorAvatar d={d} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">{d.verified && <VerifiedBadge />}{d.is_sample && <SampleBadge />}</div>
          <h3 className="mt-1 font-semibold text-[#17232E] leading-snug" data-testid="doctor-card-name">{d.full_name}</h3>
          <p className="text-xs muted">{d.qualifications}</p>
          <div className="mt-1"><RatingBadge rating={d.rating} count={d.rating_count} testId={`doctor-card-rating-${d.id}`} /></div>
          <p className="text-sm text-[#086477] mt-1">{[d.specialties.join(", "), d.experience_years != null && `${d.experience_years} yrs exp.`].filter(Boolean).join(" · ")}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs muted">
        <span className="flex items-center gap-1.5"><Languages className="h-3.5 w-3.5" />{d.languages.join(", ")}</span>
        <span className="flex items-center gap-1.5">{d.modes.includes("video") && <Video className="h-3.5 w-3.5" />}{d.modes.includes("audio") && <Phone className="h-3.5 w-3.5" />}{d.modes.join(" / ")}</span>
        <span className="col-span-2 flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" />{d.next_available ? `Next: ${fmtDT(d.next_available)}` : "No slots in the next 2 weeks"}</span>
      </div>
      <div className="flex items-center justify-between border-t border-[#E2E8ED] pt-4">
        <div><p className="text-[11px] muted">Consultation fee</p><PriceTag strike={d.strike_fee} offer={d.fee} testId="doctor-card-fee" /></div>
        <Button className="rounded-full" data-testid={`book-doctor-${d.id}`} onClick={() => nav(bookPath(d.id))} disabled={!d.next_available}>Book</Button>
      </div>
    </div>
  );
}
